import { useState} from 'react'
import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Send, CheckCircle2, Loader2, Mail, MapPin, Phone, Clock } from 'lucide-react'
import SectionHeading from './SectionHeading.tsx'


const budgets = ['< $1k', '$1k – $5k', '$5k – $15k', '$15k+']

const empty = { name: '', email: '', budget: '', message: '' }

export default function Contact() {
  const [form, setForm] = useState(empty)
  const [status, setStatus] = useState('idle')
  const [error, setError] = useState('')

  const update = (key, value: string) => {
    setForm((f) => ({ ...f, [key]: value }))
    setError('')
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError('Please fill in your name, email, and project details.')
      return
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError('Please enter a valid email address.')
      return
    }

    setStatus('loading')
    window.setTimeout(() => {
      try {
        const existing = JSON.parse(localStorage.getItem('raag_messages') || '[]')
        existing.push({ ...form, date: new Date().toISOString() })
        localStorage.setItem('raag_messages', JSON.stringify(existing))
      } catch {
        /* storage may be unavailable; ignore */
      }
      setStatus('success')
      setForm(empty)
      window.setTimeout(() => setStatus('idle'), 4000)
    }, 900)
  }

  return (
    <section id="contact" className="relative py-24 sm:py-32">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute bottom-0 right-1/4 h-80 w-80 rounded-full bg-brand-700/20 blur-[130px]" />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading
          eyebrow="Get in touch"
          title={<>Let's build something <span className="text-gradient">great together</span></>}
          subtitle="Tell us about your project and we'll get back to you within 24 hours."
        />

        <div className="mt-14 grid gap-8 lg:grid-cols-[0.85fr_1.15fr]">
          {/* Info column */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.5 }}
            className="flex flex-col gap-4"
          >
            <InfoCard icon={Mail} label="Email" value="
info@raagtechnologies.com" />
            <InfoCard
  icon={Phone}
  label="Phone"
  value={
    <>
      <div>+91 82000 71242</div>
      <div>+1 (289) 552-5748</div>
    </>
  }
/>
            <InfoCard icon={MapPin} label="Based in" value={
    <>
      Gujarat, India
      <br />
      Toronto, Canada — working worldwide
    </>
  } />
            <InfoCard icon={Clock} label="Response time" value="Within 24 hours" />
            <div className="mt-2 rounded-2xl border border-brand-500/20 bg-brand-500/5 p-5">
              <p className="text-sm leading-relaxed text-slate-300">
                Prefer email? Send us your brief at{' '}
                <span className="font-semibold text-brand-300">
                info@raagtechnologies.com</span> and we'll take it from there.
              </p>
            </div>
          </motion.div>

          {/* Form column */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.5 }}
            className="relative rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-8"
          >
            <AnimatePresence mode="wait">
              {status === 'success' ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex min-h-[420px] flex-col items-center justify-center text-center"
                >
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
                    className="grid h-16 w-16 place-items-center rounded-full bg-green-500/15 text-green-400"
                  >
                    <CheckCircle2 size={34} />
                  </motion.span>
                  <h3 className="mt-5 font-display text-2xl font-bold text-white">Message sent!</h3>
                  <p className="mt-2 max-w-sm text-sm text-slate-400">
                    Thanks for reaching out. We've received your project details and will reply within 24 hours.
                  </p>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onSubmit={handleSubmit}
                  noValidate
                  className="grid gap-5"
                >
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field label="Your name">
                      <input
                        type="text"
                        value={form.name}
                        onChange={(e) => update('name', e.target.value)}
                        placeholder="Jane Doe"
                        className="input"
                      />
                    </Field>
                    <Field label="Email address">
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) => update('email', e.target.value)}
                        placeholder="jane@company.com"
                        className="input"
                      />
                    </Field>
                  </div>

                  <Field label="Project budget">
                    <div className="flex flex-wrap gap-2">
                      {budgets.map((b) => (
                        <button
                          key={b}
                          type="button"
                          onClick={() => update('budget', b)}
                          className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                            form.budget === b
                              ? 'border-brand-500 bg-brand-500/15 text-brand-200'
                              : 'border-white/10 bg-white/5 text-slate-300 hover:border-white/20'
                          }`}
                        >
                          {b}
                        </button>
                      ))}
                    </div>
                  </Field>

                  <Field label="Tell us about your project">
                    <textarea
                      rows={5}
                      value={form.message}
                      onChange={(e) => update('message', e.target.value)}
                      placeholder="What are you building, your goals, timeline, and any links we should see..."
                      className="input resize-none"
                    />
                  </Field>

                  {error && (
                    <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand-500 px-7 py-3.5 text-base font-semibold text-ink-950 shadow-lg shadow-brand-500/30 transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-70"
                  >
                    {status === 'loading' ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send message
                        <Send size={18} className="transition-transform group-hover:translate-x-1" />
                      </>
                    )}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>

      <style>{`
        .input {
          width: 100%;
          border-radius: 0.75rem;
          border: 1px solid rgba(255,255,255,0.1);
          background: rgba(255,255,255,0.04);
          padding: 0.7rem 0.9rem;
          font-size: 0.95rem;
          color: #e2e8f0;
          transition: border-color .2s, box-shadow .2s;
        }
        .input::placeholder { color: #64748b; }
        .input:focus {
          outline: none;
          border-color: rgba(34,205,242,0.5);
          box-shadow: 0 0 0 3px rgba(34,205,242,0.15);
        }
      `}</style>
    </section>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-slate-300">{label}</span>
      {children}
    </label>
  )
}

function InfoCard({ icon: Icon, label, value }: { icon: typeof Mail; label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-brand-500/15 text-brand-300">
        <Icon size={20} />
      </span>
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
        <div className="mt-0.5 text-sm font-medium text-slate-200">{value}</div>
      </div>
    </div>
  )
}
